import React from "react";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { Badge } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";
import { useLoading } from "app/custom-hooks/loadingHook";
import { DefaultFetchService } from "app/services/fetchService";
import { addBetaBranchParam } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { MiniFlexTable } from "app/components/React/Table/FlexTable/Mini/MiniFlexTable";
import { column, options } from "app/components/React/Table/SWReactTableDefaults";
import {
    areValuesEqualAvgWithTreshold,
    BetaMessage,
    WidgetBetaMessageWrapper,
} from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/CommonComponents";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    CalculationCellWrapper,
    HeaderWrapper,
    StyledTitle,
    StyledBoxSubtitleWrapper,
    WidgetContent,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { TableLoader } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";

interface IAudienceWidgetSingleWithBeta {
    params: any;
    tableDataColumn: any;
    subtitleFilters?: any;
    hasGA?: boolean;
    isGAPrivate?: boolean;
    showBetaBranchData?: boolean;
    metric: string;
    title: string;
    titleTooltip: string;
}

export const AudienceWidgetSingleWithBeta = (props: IAudienceWidgetSingleWithBeta) => {
    const { params, tableDataColumn, subtitleFilters, hasGA, isGAPrivate, metric, title, titleTooltip } = props;
    const [visits, visitsOps] = useLoading();

    React.useEffect(() => {
        const apiController = "WebsiteOverview",
            apiWidgetType = "Table";
        const reqParams = { ...params, ShouldGetVerifiedData: hasGA };
        const endpoint = `/widgetApi/${apiController}/${metric}/${apiWidgetType}`;
        visitsOps.load(() =>
            Promise.all([
                DefaultFetchService.getInstance().get<any>(endpoint, reqParams),
                DefaultFetchService.getInstance().get<any>(endpoint, addBetaBranchParam(reqParams)),
            ]).then(([curResponse, betaResponse]) => ({
                ...curResponse,
                Data: [...betaResponse.Data.map((data) => ({ ...data, isBeta: true })), ...curResponse.Data],
            })),
        );
    }, [params, hasGA]);

    const tableData = React.useMemo(() => {
        const isGAVerified = visits.data?.KeysDataVerification?.[params.keys] ?? false;
        return {
            Records: (visits.data?.Data ?? []).map((rec) => {
                return {
                    ...rec,
                    isGAVerified,
                    isGAPrivate,
                };
            }),
        };
    }, [visits.data]);

    const isLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(visits.state);

    const isSameData = React.useMemo(() => {
        if (visits.data) {
            return [tableDataColumn].every((col: any) => {
                const normalizeValue = col.normalizeValueForComparison ?? ((v) => v);
                return areValuesEqualAvgWithTreshold(
                    (visits.data.Data ?? []).map((p) => normalizeValue(p?.[col.field])),
                );
            });
        }
    }, [visits.data]);
    const tableConfig = React.useMemo(
        () => ({
            columns: [
                column({
                    field: "Calculation",
                    displayName: "Calculation",
                    cellComponent: CalculationCell,
                }),
                tableDataColumn,
            ],
            options: options({
                tableType: "swTable--simple",
                hideBorders: true,
                loadingSize: 5,
            }),
        }),
        [],
    );

    return (
        <Flex
            height="100%"
            padding="24px"
            flexDirection="column"
            minWidth="0"
            flex="10 350px"
            width="auto"
            maxWidth="49%"
            style={{ boxSizing: "border-box", marginRight: 0, marginBottom: 24 }}
        >
            <HeaderWrapper>
                <StyledTitle>
                    <BoxTitle tooltip={i18nFilter()(titleTooltip)}>{i18nFilter()(title)}</BoxTitle>
                </StyledTitle>
            </HeaderWrapper>
            <StyledBoxSubtitleWrapper>
                <BoxSubtitle filters={subtitleFilters} />
            </StyledBoxSubtitleWrapper>
            <WidgetContent>
                {isLoading ? (
                    <Flex justifyContent="center">
                        <TableLoader rowNum={3} />
                    </Flex>
                ) : (
                    <MiniFlexTable
                        tableData={tableData}
                        tableColumns={tableConfig.columns}
                        tableOptions={tableConfig.options}
                    />
                )}
                {isSameData && (
                    <WidgetBetaMessageWrapper>
                        <BetaMessage messageKey="wa.traffic.engagement.beta.graphs.coalesce" />
                    </WidgetBetaMessageWrapper>
                )}
            </WidgetContent>
        </Flex>
    );
};

const CalculationCell = ({ row, GAVerifiedIcon }) => {
    return (
        <CalculationCellWrapper>
            {row.isBeta ? (
                <>
                    <I18n>wa.traffic.engagement.calculation.new</I18n>
                    <Badge type="beta" />
                </>
            ) : (
                <>
                    <I18n>
                        {row.isGAVerified
                            ? "wa.traffic.engagement.calculation.ga"
                            : "wa.traffic.engagement.calculation.current"}
                    </I18n>
                    {GAVerifiedIcon}
                </>
            )}
        </CalculationCellWrapper>
    );
};
